import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ManagerBatchDecideBody,
  ManagerTeamIndicatorsQuery,
  ManagerTeamQuery,
  ManagerVacationCalendarQuery,
} from "@evolua/api-client";
import { getApiClient } from "../auth/http.js";
import { useSession } from "../session/SessionContext.js";
import {
  approveDemoManagerApproval,
  batchDemoManagerApprovals,
  getDemoManagerDashboard,
  getDemoManagerTeamIndicators,
  getDemoManagerVacationCalendar,
  listDemoManagerApprovals,
  listDemoManagerTeam,
  rejectDemoManagerApproval,
  syncDemoManagerVacations,
} from "./demo-manager.js";
import { queryKeys } from "./query-keys.js";

/** Portal do gestor — escopo de árvore (FASE-06-T42/T43/T44/T47). */

export function useManagerDashboard() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.manager.dashboard(),
    queryFn: () =>
      isDemoMode ? getDemoManagerDashboard() : getApiClient().getManagerDashboard(),
  });
}

export function useManagerTeam(filters: ManagerTeamQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.manager.team(filters),
    queryFn: () =>
      isDemoMode ? listDemoManagerTeam(filters) : getApiClient().listManagerTeam(filters),
  });
}

export function useManagerApprovals() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.manager.approvals(),
    queryFn: () =>
      isDemoMode ? listDemoManagerApprovals() : getApiClient().listManagerApprovals(),
  });
}

function invalidateManager(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.manager.all });
}

export function useApproveManagerApproval() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; comment?: string; force?: boolean }) =>
      isDemoMode
        ? Promise.resolve(
            approveDemoManagerApproval(input.id, {
              ...(input.comment ? { comment: input.comment } : {}),
              ...(input.force ? { force: true } : {}),
            }),
          )
        : getApiClient().approveManagerApproval(input.id, {
            ...(input.comment ? { comment: input.comment } : {}),
            ...(input.force ? { force: true } : {}),
          }),
    onSuccess: () => invalidateManager(queryClient),
  });
}

export function useRejectManagerApproval() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; comment: string }) =>
      isDemoMode
        ? Promise.resolve(rejectDemoManagerApproval(input.id, { comment: input.comment }))
        : getApiClient().rejectManagerApproval(input.id, { comment: input.comment }),
    onSuccess: () => invalidateManager(queryClient),
  });
}

export function useBatchManagerApprovals() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ManagerBatchDecideBody) =>
      isDemoMode
        ? Promise.resolve(batchDemoManagerApprovals(body))
        : getApiClient().batchManagerApprovals(body),
    onSuccess: () => invalidateManager(queryClient),
  });
}

export function useSyncManagerVacations() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      isDemoMode
        ? Promise.resolve(syncDemoManagerVacations())
        : getApiClient().syncManagerVacations(),
    onSuccess: () => invalidateManager(queryClient),
  });
}

export function useManagerVacationCalendar(query: ManagerVacationCalendarQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.manager.vacationCalendar(query),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(getDemoManagerVacationCalendar(query))
        : getApiClient().getManagerVacationCalendar(query),
  });
}

export function useManagerTeamIndicators(query: ManagerTeamIndicatorsQuery = {}) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.manager.teamIndicators(query),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(getDemoManagerTeamIndicators(query))
        : getApiClient().getManagerTeamIndicators(query),
  });
}
